"use client"

import { useEffect, useId, useState } from "react"
import { createPortal } from "react-dom"
import { m, AnimatePresence } from "framer-motion"
import { CloseIcon } from "@/components/ui/icons"

/**
 * Generic centered modal — portal + overlay + scale/fade animation + a
 * viewport-fixed close button. Extracted from the product-info modal so any
 * surface can drop arbitrary content into the same dialog shell (test-page
 * member-pricing info, the blood membership-vs-one-off comparison, …).
 *
 * Controlled: the caller owns `open` and `onClose`. The portal mounts to
 * <body> so the dialog escapes transformed/`isolation` ancestors (a hero's
 * framer-motion wrapper, a card's stacking context) that would otherwise trap
 * `position: fixed`. Scroll-locks the body with scrollbar-width compensation
 * (no-op on overlay-scrollbar platforms) and closes on overlay click + Escape.
 */
export function InfoModal({
  open,
  onClose,
  label,
  maxWidth = 520,
  children,
}: {
  open: boolean
  onClose: () => void
  /** Accessible dialog name (aria-label). */
  label: string
  /** Dialog max-width in px. */
  maxWidth?: number
  children: React.ReactNode
}) {
  // Defer portal mount until after hydration — document.body isn't available
  // during SSR.
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  const descId = useId()

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    // Measure the real scrollbar width before locking, then pad the body so the
    // page doesn't shift when the document scrollbar disappears (Radix pattern).
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth
    const prevOverflow = document.body.style.overflow
    const prevPaddingRight = document.body.style.paddingRight
    document.body.style.overflow = "hidden"
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`
    }
    window.addEventListener("keydown", handler)
    return () => {
      document.body.style.overflow = prevOverflow
      document.body.style.paddingRight = prevPaddingRight
      window.removeEventListener("keydown", handler)
    }
  }, [open, onClose])

  if (!mounted) return null

  return createPortal(
    /* safe-animate-presence: conditional-mount — child only renders when `open=true` */
    <AnimatePresence>
      {open && (
        <>
          {/* Overlay doubles as the scroll container */}
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] overflow-y-auto overscroll-contain bg-black/40 backdrop-blur-sm"
            onClick={onClose}
          >
            <div className="flex min-h-full items-start justify-center p-3 sm:items-center sm:p-6 sm:py-16">
              <m.div
                initial={{ opacity: 0, scale: 0.96, y: 16 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: 16 }}
                transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                role="dialog"
                aria-modal="true"
                aria-label={label}
                aria-describedby={descId}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full overflow-hidden rounded-[24px] bg-white p-6 sm:p-8"
                style={{ maxWidth }}
              >
                <span id={descId} className="sr-only">
                  Press Escape to close
                </span>
                {children}
              </m.div>
            </div>
          </m.div>
          {/* Close button — fixed to the viewport, always reachable */}
          <m.button
            type="button"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
            onClick={onClose}
            className="fixed top-4 right-6 z-[110] flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm transition-colors hover:bg-black/70 sm:top-5 sm:right-7"
            aria-label="Close"
            autoFocus
          >
            <CloseIcon width={22} height={22} />
          </m.button>
        </>
      )}
    </AnimatePresence>,
    document.body
  )
}
